import React, { useEffect, useState } from 'react';
import {
  getAllSmsTemplates,
  createSmsTemplate,
  updateSmsTemplate,
  deleteSmsTemplate,
} from '../../services/TemplatesService';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

function SmsTemplates() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewTemplate, setPreviewTemplate] = useState(null);

  useEffect(() => {
    fetchTemplates();
  }, []);

  async function fetchTemplates() {
    setLoading(true);
    setError(null);
    try {
      const data = await getAllSmsTemplates();
      // Filter for SMS templates (assuming they have a type field or we can identify them)
      // For now, we'll use all templates but this can be adjusted based on your API
      setTemplates(data);
    } catch (err) {
      setError('Failed to load SMS templates');
    }
    setLoading(false);
  }

  function handleEdit(template) {
    setEditingTemplate(template);
    setSidebarOpen(true);
  }

  function handleCreate() {
    setEditingTemplate(null);
    setSidebarOpen(true);
  }

  function handleDelete(template) {
    setTemplateToDelete(template);
    setShowDeleteModal(true);
  }

  async function confirmDelete() {
    try {
      await deleteSmsTemplate(templateToDelete.id);
      toast.success('SMS template deleted');
      setShowDeleteModal(false);
      setTemplateToDelete(null);
      fetchTemplates();
    } catch (err) {
      toast.error('Failed to delete SMS template');
    }
  }

  return (
    <div className="mt-4" style={{ paddingLeft: 25, paddingRight: 25, width: '100%' }}>
      <ToastContainer />
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="mb-0">SMS Templates</h4>
        <button className="btn btn-primary" onClick={handleCreate}>
          + Create SMS Template
        </button>
      </div>
      {loading ? (
        <div>Loading SMS templates...</div>
      ) : error ? (
        <div className="alert alert-danger">{error}</div>
      ) : (
        <div className="table-responsive">
          <table className="table align-middle">
            <thead>
              <tr>
                <th>Name</th>
                <th>Message</th>
                <th>Created</th>
                <th>Updated</th>
                <th style={{ width: 160 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {templates.length === 0 ? (
                <tr><td colSpan={5} className="text-center">No SMS templates found.</td></tr>
              ) : (
                templates.map(t => (
                  <tr key={t.id}>
                    <td>{t.templateName}</td>
                    <td>
                      <div title={t.message ? t.message.replace(/<[^>]*>/g, '') : ''} style={{ maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'pointer' }}>
                        {t.message ? t.message.replace(/<[^>]*>/g, '') : ''}
                      </div>
                    </td>
                    <td>{t.createdAt ? new Date(t.createdAt).toLocaleString() : ''}</td>
                    <td>{t.updatedAt ? new Date(t.updatedAt).toLocaleString() : ''}</td>
                    <td>
                      <div className="btn-group">
                        <button className="btn btn-sm btn-outline-primary me-2" onClick={() => handleEdit(t)} title="Edit">
                          <i className="bi bi-pencil"></i>
                        </button>
                        <button className="btn btn-sm btn-outline-primary me-2" onClick={() => { setPreviewTemplate(t); setPreviewOpen(true); }} title="Preview">
                          <i className="bi bi-eye"></i>
                        </button>
                        <button className="btn btn-sm btn-outline-danger" onClick={() => handleDelete(t)} title="Delete">
                          <i className="bi bi-trash"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
      {/* Sidebar for create/edit */}
      {sidebarOpen && (
        <SmsTemplateSidebar
          isOpen={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          template={editingTemplate}
          onSaved={() => {
            setSidebarOpen(false);
            fetchTemplates();
          }}
        />
      )}
      {/* Delete confirmation modal */}
      {showDeleteModal && (
        <div className="modal fade show d-block" tabIndex="-1" style={{ background: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Confirm Delete</h5>
                <button type="button" className="btn-close" onClick={() => setShowDeleteModal(false)}></button>
              </div>
              <div className="modal-body">
                Are you sure you want to delete SMS template <strong>{templateToDelete?.templateName}</strong>?
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowDeleteModal(false)}>Cancel</button>
                <button className="btn btn-danger" onClick={confirmDelete}>Delete</button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Preview Modal */}
      {previewOpen && previewTemplate && (
        <div className="modal fade show d-block" tabIndex="-1" style={{ background: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">SMS Template Preview</h5>
                <button type="button" className="btn-close" onClick={() => setPreviewOpen(false)}></button>
              </div>
              <div className="modal-body">
                <h6>Name: {previewTemplate.templateName}</h6>
                <div className="border p-3" style={{ minHeight: 100 }}>
                  <div>{previewTemplate.body ? previewTemplate.body.replace(/<[^>]*>/g, '') : ''}</div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setPreviewOpen(false)}>Close</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SmsTemplateSidebar({ isOpen, onClose, template, onSaved }) {
  const isEdit = !!template;
  const [templateName, setTemplateName] = useState(template?.templateName || '');
  const [message, setMessage] = useState(template?.body ? template.body.replace(/<[^>]*>/g, '') : '');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setTemplateName(template?.templateName || '');
    setMessage(template?.body ? template.body.replace(/<[^>]*>/g, '') : '');
  }, [template]);

  async function handleSubmit(e) {
    e.preventDefault();
    setSaving(true);
    try {
      if (isEdit) {
        await updateSmsTemplate(template.id, {
          templateName,
          message: message,
        });
        toast.success('SMS template updated');
      } else {
        await createSmsTemplate({
          templateName,
          message: message,
        });
        toast.success('SMS template created');
      }
      onSaved();
    } catch (err) {
      toast.error('Failed to save SMS template');
    }
    setSaving(false);
  }

  return (
    <div className={`position-fixed top-0 end-0 bottom-0 bg-white shadow overflow-auto ${!isOpen ? 'd-none' : ''}`}
      style={{ width: '400px', zIndex: 1045 }}>
      <div className="p-3 border-bottom d-flex justify-content-between align-items-center">
        <h5 className="mb-0">{isEdit ? 'Edit SMS Template' : 'Create SMS Template'}</h5>
        <button type="button" className="btn-close" onClick={onClose}></button>
      </div>
      <div className="p-3">
        <form onSubmit={handleSubmit}>
          <div className="mb-3">
            <label className="form-label fw-semibold">Template Name *</label>
            <input
              type="text"
              className="form-control"
              value={templateName}
              onChange={e => setTemplateName(e.target.value)}
              required
            />
          </div>
          <div className="mb-3">
            <label className="form-label fw-semibold">Message *</label>
            <textarea
              className="form-control"
              rows="6"
              value={message}
              onChange={e => setMessage(e.target.value)}
              placeholder="Enter your SMS message template..."
              required
            />
            <small className="text-muted">SMS messages are limited to 160 characters per message.</small>
          </div>
          
          <div className="d-flex justify-content-end gap-2">
            <button type="button" className="btn btn-outline-secondary" onClick={onClose} disabled={saving}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving...' : isEdit ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default SmsTemplates; 